class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    // The header cart icon is a plain link to the cart page; the drawer only
    // opens after adding to cart.
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.addedItem = null;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Re-initialize accelerated checkout buttons injected with the new drawer HTML
    window?.Shopify?.PaymentButton?.init();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.addedItem = parsedState.items?.[0] ?? parsedState;
      this.highlightAddedItem();
      this.announceAddedItem();
      this.open();
    });
  }

  // Marks the line just added (a new line at the top, or an existing one whose
  // quantity went up) with a fading highlight and scrolls it into view. Called
  // again by CartItems.onCartUpdate, as that re-renders the items right after
  // an add (and, in the local theme preview, is the first render that has them
  // at all).
  highlightAddedItem() {
    const key = this.addedItem?.key;
    const row = key && this.querySelector(`.cart-item[data-key="${CSS.escape(key)}"]`);
    if (!row || row.classList.contains('cart-item--added')) return;

    row.classList.add('cart-item--added');

    // Desktop scrolls the items list, mobile the whole drawer
    const scroller = [row.closest('cart-drawer-items'), row.closest('.drawer__inner')].find(
      (element) => element && element.scrollHeight > element.clientHeight
    );
    if (scroller) {
      const offset = row.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
      scroller.scrollTop += offset - (scroller.clientHeight - row.offsetHeight) / 2;
    }
  }

  announceAddedItem() {
    const item = this.addedItem;
    const status = document.getElementById('CartDrawer-AddedStatus');
    if (!item?.product_title || !status) return;

    const name = item.product_has_only_default_variant
      ? item.product_title
      : [item.product_title, item.variant_title].filter(Boolean).join(', ');
    // Cleared first, so adding the same product again is announced again
    status.textContent = '';
    setTimeout(() => {
      status.textContent = status.dataset.message.replace('[product]', name);
    }, 100);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
